import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Flag, ImageUp, Loader2, Pencil, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { friendlyErrorMessage } from "@/platform/lib/error-messages";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { cn } from "@/platform/lib/cn";
import { useSession } from "@/platform/auth/use-session";
import {
  getUspCorpusStats,
  listUspQuestions,
  patchUspQuestion,
  setUspQuestionStatus,
  type UspAdminQuestion,
  type UspQaFilters,
  type UspQuestionStatus,
} from "@/tenants/usp/features/admin/qa-api";
import { replaceUspPageImage, resolveUspPaperPage } from "@/platform/admin/api";

const STATUS_OPTS: Array<UspQuestionStatus | "all"> = ["all", "draft", "review", "approved", "archived"];
const USP_STATUS_LABELS: Record<string, string> = {
  draft: "marcadas como rascunho",
  review: "marcadas para revisão",
  approved: "aprovadas",
  archived: "arquivadas",
};
const KIND_OPTS = ["all", "objective", "discursive", "essay"] as const;
const STATUS_TONE: Record<string, string> = {
  draft: "222 30% 50%",
  review: "40 85% 45%",
  approved: "155 60% 38%",
  archived: "0 0% 55%",
};

export function AdminUspQaPage() {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<UspQaFilters>({ status: "draft", page: 1, pageSize: 15 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [editing, setEditing] = useState<UspAdminQuestion | null>(null);

  const statsQuery = useQuery({ queryKey: ["usp-qa-stats"], queryFn: getUspCorpusStats });
  const listQuery = useQuery({
    queryKey: ["usp-qa-list", filters],
    queryFn: () => listUspQuestions(filters),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: UspQuestionStatus }) =>
      setUspQuestionStatus(id, status, session?.user.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usp-qa-list"] });
      queryClient.invalidateQueries({ queryKey: ["usp-qa-stats"] });
    },
    onError: (e) => toast.error(friendlyErrorMessage(e, "Falha ao atualizar")),
  });

  const stats = statsQuery.data;
  const rows = listQuery.data?.rows ?? [];
  const total = listQuery.data?.total ?? 0;
  const pageSize = filters.pageSize ?? 15;
  const page = filters.page ?? 1;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function update(patch: Partial<UspQaFilters>) {
    setFilters((f) => ({ ...f, ...patch, page: patch.page ?? 1 }));
    setSelectedIds(new Set());
  }

  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function bulkSetStatus(status: UspQuestionStatus) {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    setBulkRunning(true);
    const toastId = toast.loading(
      `Atualizando ${ids.length} questão${ids.length > 1 ? "s" : ""}…`,
    );
    try {
      const chunkSize = 10;
      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        await Promise.all(chunk.map((id) => setUspQuestionStatus(id, status, session?.user.id)));
      }
      const statusLabel = USP_STATUS_LABELS[status] ?? status;
      toast.success(
        `${ids.length} questão${ids.length > 1 ? "s" : ""} ${statusLabel}.`,
        { id: toastId },
      );
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ["usp-qa-list"] });
      queryClient.invalidateQueries({ queryKey: ["usp-qa-stats"] });
    } catch (err) {
      toast.error(friendlyErrorMessage(err, "Falha ao aplicar em lote."), { id: toastId });
    } finally {
      setBulkRunning(false);
    }
  }

  return (
    <Container size="wide" className="py-12 md:py-16">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] hover:text-[color:var(--color-foreground)]">
        <ArrowLeft className="size-3.5" />
        Admin
      </Link>

      <header className="mt-6 mb-8">
        <Badge variant="accentSolid" className="gap-2">
          <Sparkles className="size-3" />
          QA · FuvestPro USP
        </Badge>
        <h1 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
          Revisão do corpus Fuvest
        </h1>
        {stats && (
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.entries(stats.byStatus).map(([s, n]) => (
              <Badge key={s} variant="outline" className="gap-1.5">
                <span className="size-1.5 rounded-full" style={{ backgroundColor: `hsl(${STATUS_TONE[s] ?? "222 30% 50%"})` }} />
                {s}: {n}
              </Badge>
            ))}
            <Badge variant="outline">total: {stats.total}</Badge>
          </div>
        )}
      </header>

      {/* Filtros */}
      <div className="mb-6 flex flex-wrap gap-3">
        <select
          value={filters.status ?? "all"}
          onChange={(e) => update({ status: e.target.value as UspQuestionStatus | "all" })}
          className="rounded-lg border border-[--color-border] bg-[color:var(--color-background)] px-3 py-1.5 text-[length:var(--text-body-sm)]"
        >
          {STATUS_OPTS.map((s) => <option key={s} value={s}>status: {s}</option>)}
        </select>
        <select
          value={filters.kind ?? "all"}
          onChange={(e) => update({ kind: e.target.value as (typeof KIND_OPTS)[number] })}
          className="rounded-lg border border-[--color-border] bg-[color:var(--color-background)] px-3 py-1.5 text-[length:var(--text-body-sm)]"
        >
          {KIND_OPTS.map((k) => <option key={k} value={k}>tipo: {k}</option>)}
        </select>
        <select
          value={String(filters.year ?? "all")}
          onChange={(e) => update({ year: e.target.value === "all" ? "all" : Number(e.target.value) })}
          className="rounded-lg border border-[--color-border] bg-[color:var(--color-background)] px-3 py-1.5 text-[length:var(--text-body-sm)]"
        >
          {["all", 2024, 2025, 2026].map((y) => <option key={y} value={String(y)}>ano: {y}</option>)}
        </select>
        <input
          type="search"
          placeholder="Buscar no enunciado…"
          defaultValue={filters.search ?? ""}
          onKeyDown={(e) => { if (e.key === "Enter") update({ search: (e.target as HTMLInputElement).value }); }}
          className="flex-1 min-w-48 rounded-lg border border-[--color-border] bg-[color:var(--color-background)] px-3 py-1.5 text-[length:var(--text-body-sm)]"
        />
      </div>

      {/* Barra de ações em massa */}
      {rows.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-3 text-[length:var(--text-body-sm)]">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={rows.length > 0 && rows.every((r) => selectedIds.has(r.id))}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedIds((prev) => {
                    const next = new Set(prev);
                    for (const r of rows) next.add(r.id);
                    return next;
                  });
                } else {
                  setSelectedIds(new Set());
                }
              }}
              className="size-4 cursor-pointer accent-[var(--color-accent)]"
            />
            {selectedIds.size > 0
              ? `${selectedIds.size} selecionada${selectedIds.size > 1 ? "s" : ""}`
              : "Selecionar todas desta página"}
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="primary"
              onClick={() => bulkSetStatus("approved")}
              disabled={selectedIds.size === 0 || bulkRunning}
              loading={bulkRunning}
            >
              Aprovar selecionadas
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => bulkSetStatus("review")}
              disabled={selectedIds.size === 0 || bulkRunning}
            >
              Marcar p/ revisão
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => bulkSetStatus("archived")}
              disabled={selectedIds.size === 0 || bulkRunning}
            >
              Arquivar
            </Button>
            {selectedIds.size > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                Limpar
              </Button>
            )}
          </div>
        </div>
      )}

      {listQuery.isLoading ? (
        <Loader2 className="size-6 animate-spin text-[color:var(--color-muted-foreground)]" />
      ) : rows.length === 0 ? (
        <p className="text-[color:var(--color-muted-foreground)]">Nenhuma questão com esses filtros.</p>
      ) : (
        <ul className="space-y-4">
          {rows.map((q) => (
            <li
              key={q.id}
              className={cn(
                "rounded-2xl border bg-[color:var(--color-card)] p-6 shadow-[var(--shadow-sm)]",
                selectedIds.has(q.id)
                  ? "border-[color:var(--color-accent)]/60 ring-2 ring-[color:var(--color-accent)]/30"
                  : "border-[--color-border]",
              )}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(q.id)}
                    onChange={() => toggleSelected(q.id)}
                    aria-label="Selecionar para ação em lote"
                    className="size-4 cursor-pointer accent-[var(--color-accent)] mr-1"
                  />
                  <Badge variant="outline" className="capitalize">{q.discipline}</Badge>
                  <span className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                    Fuvest {q.year} · {q.phase === "1" ? "1ª fase" : `2ª/dia${q.day}`} · Q{q.number} · {q.kind}
                  </span>
                  <Badge style={{ backgroundColor: `hsl(${STATUS_TONE[q.status] ?? "222 30% 50%"})`, color: "white", border: "none" }}>
                    {q.status}
                  </Badge>
                  {q.llm_confidence != null && (
                    <span className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">conf {q.llm_confidence}</span>
                  )}
                </div>
              </div>

              <p className="mt-4 whitespace-pre-line text-[length:var(--text-body-sm)] leading-relaxed">{q.stem}</p>

              {q.alternatives && !("items" in q.alternatives) && (
                <ul className="mt-3 space-y-1 text-[length:var(--text-body-sm)]">
                  {Object.entries(q.alternatives as Record<string, string>).filter(([k]) => k.length === 1).map(([k, v]) => (
                    <li key={k} className={cn(q.correct_answer === k && "font-semibold")} style={q.correct_answer === k ? { color: "hsl(155 60% 38%)" } : undefined}>
                      ({k}) {v} {q.correct_answer === k && "✓"}
                    </li>
                  ))}
                </ul>
              )}

              {q.expected_answer && (
                <details className="mt-3 text-[length:var(--text-body-sm)]">
                  <summary className="cursor-pointer text-[color:var(--color-muted-foreground)]">Abordagem esperada</summary>
                  <p className="mt-2 whitespace-pre-line text-[color:var(--color-muted-foreground)]">{q.expected_answer}</p>
                </details>
              )}

              {(q.has_image || q.has_graph || q.has_table) && q.pageImageUrl && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-[length:var(--text-body-sm)] text-[color:var(--color-accent)]">Ver página original</summary>
                  <img src={q.pageImageUrl} alt={`Fuvest ${q.year} Q${q.number}`} loading="lazy" className="mt-2 w-full max-w-2xl rounded-lg border border-[--color-border]" />
                </details>
              )}

              {/* classificação */}
              <div className="mt-4 flex flex-wrap gap-1.5">
                {[q.topic, q.subtopic, q.cognitive_skill, q.question_type, q.pattern, q.difficulty].filter(Boolean).map((t, i) => (
                  <span key={i} className="rounded-full bg-[color:var(--color-muted)] px-2.5 py-0.5 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">{t}</span>
                ))}
              </div>

              {/* ações */}
              <div className="mt-5 flex flex-wrap gap-2 border-t border-[--color-border] pt-4">
                <Button size="sm" variant="primary" onClick={() => statusMut.mutate({ id: q.id, status: "approved" })} disabled={statusMut.isPending || q.status === "approved"}>
                  <Check className="size-3.5" /> Aprovar
                </Button>
                <Button size="sm" variant="outline" onClick={() => statusMut.mutate({ id: q.id, status: "review" })} disabled={statusMut.isPending}>
                  <Flag className="size-3.5" /> Revisar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => statusMut.mutate({ id: q.id, status: "archived" })} disabled={statusMut.isPending}>
                  <X className="size-3.5" /> Arquivar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(q)}>
                  <Pencil className="size-3.5" /> Editar
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* paginação */}
      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => update({ page: page - 1 })}>Anterior</Button>
          <span className="text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => update({ page: page + 1 })}>Próxima</Button>
        </div>
      )}
      {editing && (
        <EditQuestionModal
          question={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            queryClient.invalidateQueries({ queryKey: ["usp-qa-list"] });
          }}
        />
      )}
    </Container>
  );
}

function EditQuestionModal({
  question,
  onClose,
  onSaved,
}: {
  question: UspAdminQuestion;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [stem, setStem] = useState(question.stem);
  const [correct, setCorrect] = useState(question.correct_answer ?? "");
  const [discipline, setDiscipline] = useState(question.discipline);
  const [expectedAnswer, setExpectedAnswer] = useState(question.expected_answer ?? "");
  const [saving, setSaving] = useState(false);

  // Imagem de página: resolvida via exam_paper_id + page_number.
  const [paperPage, setPaperPage] = useState<{
    id: string;
    publicUrl: string;
  } | null>(null);
  const [paperPageLoading, setPaperPageLoading] = useState(false);
  const [imgVersion, setImgVersion] = useState(0); // força recarregar imagem após upload
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!question.exam_paper_id || question.page_number == null) return;
    setPaperPageLoading(true);
    resolveUspPaperPage(question.exam_paper_id, question.page_number)
      .then((p) => p && setPaperPage({ id: p.id, publicUrl: p.publicUrl }))
      .catch(() => null)
      .finally(() => setPaperPageLoading(false));
  }, [question.exam_paper_id, question.page_number]);

  async function handleImageReplace(file: File) {
    if (!paperPage) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem acima de 5 MB. Comprima antes de enviar.");
      return;
    }
    setUploadingImage(true);
    const toastId = toast.loading("Enviando imagem…");
    try {
      await replaceUspPageImage({ paperPageId: paperPage.id, file });
      toast.success("Imagem substituída. O cache do CDN pode levar alguns segundos.", {
        id: toastId,
      });
      setImgVersion((v) => v + 1);
    } catch (err) {
      toast.error(friendlyErrorMessage(err, "Falha ao substituir imagem."), {
        id: toastId,
      });
    } finally {
      setUploadingImage(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await patchUspQuestion(question.id, {
        stem: stem.trim(),
        correct_answer: correct.trim() || null,
        discipline,
        expected_answer: expectedAnswer.trim() || null,
      });
      toast.success("Questão atualizada.");
      onSaved();
    } catch (err) {
      toast.error(friendlyErrorMessage(err, "Falha ao salvar."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display text-[length:var(--text-body-lg)] font-semibold">
          Editar questão · Fuvest {question.year} · Q{question.number}
        </h3>
        <p className="mt-1 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
          Alterações aplicam direto em <code>usp_questions</code>. Use com cuidado.
        </p>

        <div className="mt-5 space-y-4">
          <div>
            <label className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
              Enunciado
            </label>
            <textarea
              value={stem}
              onChange={(e) => setStem(e.target.value)}
              rows={8}
              className="mt-1 w-full rounded-lg border border-[--color-border] bg-[color:var(--color-background)] p-3 text-[length:var(--text-body-sm)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                Disciplina
              </label>
              <input
                value={discipline}
                onChange={(e) => setDiscipline(e.target.value)}
                className="mt-1 w-full rounded-lg border border-[--color-border] bg-[color:var(--color-background)] p-2.5 text-[length:var(--text-body-sm)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]"
              />
            </div>
            <div>
              <label className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                Resposta correta {question.kind === "objective" ? "(A-E)" : "(opcional)"}
              </label>
              <input
                value={correct}
                onChange={(e) => setCorrect(e.target.value)}
                className="mt-1 w-full rounded-lg border border-[--color-border] bg-[color:var(--color-background)] p-2.5 text-[length:var(--text-body-sm)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]"
                maxLength={question.kind === "objective" ? 1 : undefined}
              />
            </div>
          </div>
          {question.kind !== "objective" && (
            <div>
              <label className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                Abordagem esperada
              </label>
              <textarea
                value={expectedAnswer}
                onChange={(e) => setExpectedAnswer(e.target.value)}
                rows={5}
                className="mt-1 w-full rounded-lg border border-[--color-border] bg-[color:var(--color-background)] p-3 text-[length:var(--text-body-sm)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]"
              />
            </div>
          )}

          {/* Reupload de imagem da página */}
          {question.exam_paper_id && question.page_number != null && (
            <div className="rounded-lg border border-[--color-border] bg-[color:var(--color-paper)] p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                    Imagem da página
                  </p>
                  <p className="mt-1 text-[length:var(--text-body-sm)]">
                    Página {question.page_number}. Substituir mantém o mesmo storage_path.
                  </p>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleImageReplace(f);
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  loading={uploadingImage}
                  disabled={!paperPage || paperPageLoading}
                >
                  <ImageUp className="size-3.5" />
                  Substituir imagem
                </Button>
              </div>
              {paperPageLoading ? (
                <div className="mt-3 flex items-center gap-2 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                  <Loader2 className="size-3.5 animate-spin" /> Carregando imagem…
                </div>
              ) : paperPage ? (
                <img
                  src={`${paperPage.publicUrl}?v=${imgVersion}`}
                  alt={`Página ${question.page_number}`}
                  className="mt-3 max-h-72 w-auto rounded border border-[--color-border]"
                  loading="lazy"
                />
              ) : (
                <p className="mt-3 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                  Sem render disponível para esta página em usp_paper_pages.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>
            Salvar alterações
          </Button>
        </div>
      </div>
    </div>
  );
}
